import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { 
    Save, ArrowLeft, Plus, Trash2, Shield, Wrench, DollarSign, 
    ChevronLeft, ChevronRight, Layout, List, Paperclip, Settings, Minus, Type
} from 'lucide-react';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';
import AttachmentsModal from './AttachmentsModal';
import SmartPrintButton from './PrintButton';

const CraftDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isNew = !id || id === 'new';
    
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const { canCreate, canUpdate, canDelete } = usePermissions('crafts');
    const fontContext = useFontSize();
    const fontSize = fontContext?.fontSize || 13;

    const [activeTab, setActiveTab] = useState('MAIN');
    const [loading, setLoading] = useState(false);
    const [isDocModalOpen, setIsDocModalOpen] = useState(false);
    const [count, setCount] = useState(0); 
    const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });

    const session = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionSite = session.site_id || '';
    const sessionOrg = session.org_id || '';  

    const [data, setData] = useState({
        craft_code: '', description: '', standard_rate: 0.0,
        status: 'ACTIVE', org_id: sessionOrg, site_id: sessionSite, skills: []
    });

    const isEditable = isNew ? canCreate : canUpdate;

    const loadData = useCallback(async () => {
        if (isNew) return;
        try {
            const res = await axios.get(`/api/crafts/${id}`);
            setData({ ...res.data, skills: res.data.skills || [] });
        } catch (err) { toast.error(t('common.load_fail')); }
    }, [id, isNew, t]);

    useEffect(() => { loadData(); }, [loadData]);

    useEffect(() => {
        if (isNew && data.org_id) {
            setData(prev => ({
                ...prev, description: '', status: 'ACTIVE', site_id: sessionSite, org_id: sessionOrg,
                standard_rate: 0.0, skills: []
            }));
        }
    }, [isNew, sessionSite, sessionOrg]);

    const loadNavigation = useCallback(async () => {
        if (isNew) return;
        const searchParams = new URLSearchParams(location.search); 
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/crafts/${id}/navigation`,{
                params: {
                    all_sites: searchParams.get('all_sites') || "false",
                    user_siteid: sessionSite, orgid: sessionOrg,
                    filter_siteid: searchParams.get('siteid') || "", 
                    filter_craft_code: searchParams.get('craft_code') || "",
                    description: searchParams.get('description') || "",
                    sortBy: searchParams.get('sortBy') || "craft_code",
                    sortDir: searchParams.get('sortDir') || "asc"
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setNav(res.data || { first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
        } catch (err) { setNav({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 }); }
    }, [id, isNew, location.search, sessionSite, sessionOrg]);

    useEffect(() => { loadNavigation(); }, [loadNavigation]);

    const fetchCount = useCallback(async () => {
        if (!data.craft_code || isNew) { setCount(0); return; }
        try {
            const res = await axios.get(`/api/attachments/count/CRAFT/${data.craft_code}`);
            setCount(res.data.count || 0);
        } catch (err) { console.error("Error fetching attachment count:", err); }
    }, [data.craft_code, isNew]);

    useEffect(() => { fetchCount(); }, [fetchCount]);

    const addSkill = () => {
        setData({ ...data, skills: [...data.skills, { skill_level: '', standard_rate: 0.0 }] });
    };

    const removeSkill = (index) => {
        const newList = [...data.skills];
        newList.splice(index, 1);
        setData({ ...data, skills: newList });
    };

    useEffect(() => {
        if (data.skills && data.skills.length > 0) {
            const total = data.skills.reduce((sum, skill) => sum + (parseFloat(skill.standard_rate) || 0), 0);
            if (total !== data.standard_rate) {
                setData(prev => ({ ...prev, standard_rate: total }));
            }
        }
    }, [data.skills]);

    const handleSave = async () => {
        if (!isEditable) return;
        try {
            setLoading(true);
            await axios.post('/api/crafts/save', data);
            toast.success(t('common.save_success'));
            if (isNew) navigate(`/crafts/${data.craft_code}`);
        } catch (err) { 
            toast.error(t('common.save_fail') + ": " + (err.response?.data?.detail || "System Error")); 
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        const result = await Swal.fire({
            title: t('common.are_you_sure'), text: t('common.cannot_be_undone'),
            icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33',
            cancelButtonColor: '#1a3353', confirmButtonText: t('common.yes')
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`/api/crafts/${data.craft_code}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                toast.success(t('common.delete_success'));
                navigate('/crafts'); 
            } catch (err) { toast.error(err.response?.data?.detail || t('common.delete_fail')); }
        }
    };  

    useEffect(() => {
        const triggerSave = () => handleSave();
        window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
        return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    }, [handleSave]);

    useEffect(() => {
        const triggerDelete = () => handleDelete();
        window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
        return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    }, [handleDelete]);

    const goTo = (target) => target && navigate(`/crafts/${target}${location.search}`);

    return (
        <div className={`flex flex-col h-full bg-[#f3f4f6] text-[11px] uppercase tracking-tighter ${isRTL ? 'font-cairo dir-rtl text-right' : 'text-left'}`} style={{ fontSize: `${fontSize}px` }}>
            
            {!isNew && (
            <AttachmentsModal 
                isOpen={isDocModalOpen} onClose={() => {setIsDocModalOpen(false);fetchCount();}} 
                ownertable="CRAFT" ownerid={data.craft_code} 
            />)}

            {/* --- TOP TOOLBAR --- */}
            <div className="bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/crafts')} className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-blue-200 active:scale-90">
                        <ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''} />
                    </button>
                    {!isNew && (
                        <div className="flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1">
                            <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
                            <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                            <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1"><span className="text-orange-400">{nav.current_pos}</span> / {nav.total}</div>
                            <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                            <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {isEditable && (
                    <button onClick={handleSave} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] transition-all shadow-md active:scale-95 uppercase">
                        <Save size={14} /> {loading ? t('common.saving') : t('common.save')}
                    </button>
                    )}
                    
                    <div className="h-6 w-[1px] bg-white/10 mx-1"></div>
                    <SmartPrintButton moduleName="CRAFT" refId={data.craft_code} />
                    <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-blue-200 transition-colors" title={t('common.attachments')}>
                        <Paperclip size={16} />
                        {count > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black">{count}</span>}
                    </button>
                    {!isNew && canDelete && (
                        <button onClick={handleDelete} className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors" title={t('common.delete')}>
                            <Trash2 size={16}/>
                        </button>
                    )}
                    
                    {/* Font Size Controls */}
                    <div className={`flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 shadow-inner ${isRTL ? 'mr-4 flex-row-reverse' : 'ml-4'}`}>
                        <button onClick={() => fontContext?.changeFontSize(-1)} className="hover:text-orange-400 transition-colors"><Minus size={14} /></button>
                        <div className="flex items-center gap-1 min-w-[40px] justify-center text-blue-200">
                            <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
                        </div>
                        <button onClick={() => fontContext?.changeFontSize(1)} className="hover:text-orange-400 transition-colors"><Plus size={14} /></button>
                    </div>
                </div>
            </div>

            {/* --- DOCUMENT HEADER --- */}
            <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-12 shadow-sm z-20 shrink-0">
                <HeaderField label={t('crafts.tables.craftCode')} value={data.craft_code} onChange={v => setData({...data, craft_code: v})} isBold width="w-32" disabled={!isNew} isRTL={isRTL} />
                <HeaderField label={t('crafts.tables.description')} value={data.description} onChange={v => setData({...data, description: v})} width="w-[400px]" disabled={!isEditable} placeholder={t('common.enterDesc')} isRTL={isRTL} />
                <HeaderField label={t('crafts.tables.site')} value={data.site_id || t('common.global')} disabled width="w-24" isRTL={isRTL} />
            </div>

            {/* --- TABS --- */}
            <div className="flex bg-white border-b border-gray-100 px-4 shrink-0">
                {[
                    { id: 'MAIN', icon: Layout, label: t('common.main') }, 
                    { id: 'SKILLS_AND_LEVELS', icon: List, label: t('crafts.tabs.skills') }
                ].map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)} 
                        className={`px-8 py-3 flex items-center gap-2 transition-all text-[10px] font-black uppercase tracking-tighter
                        ${activeTab === tab.id ? 'border-b-2 border-[#1a3353] text-[#1a3353] bg-gray-50' : 'text-gray-400 hover:text-gray-600'} `}>
                        <tab.icon size={13}/> {tab.label}
                    </button>
                ))}
            </div>

            {/* --- CONTENT AREA --- */}
            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-[#f8f9fb]">
                <div className="max-w-5xl mx-auto animate-in fade-in duration-500 pb-20">
                    
                    {activeTab === 'MAIN' && (
                        <div className="grid grid-cols-2 gap-6">
                            <Section title={t('crafts.sections.costing')} icon={DollarSign} isRTL={isRTL}>
                                <div className="space-y-4">
                                    <div className="flex flex-col border-b border-gray-100 py-1.5 px-1 bg-white/40 shadow-inner rounded-sm">
                                        <label className="text-[0.8em] font-black text-gray-400 uppercase tracking-widest italic">{t('crafts.fields.stdHourlyRate')}</label>
                                        <div className="font-mono font-black uppercase tracking-tighter text-green-700 text-[14px] mt-0.5 bg-green-50 w-fit px-3 py-1 rounded border border-green-100">
                                            {parseFloat(data.standard_rate || 0).toLocaleString()}
                                        </div>
                                        <p className="text-[9px] text-gray-400 mt-2 lowercase italic">* {t('crafts.messages.autoCalc')}</p>
                                    </div>
                                </div>
                            </Section>

                            <Section title={t('crafts.sections.control')} icon={Shield} isRTL={isRTL}>
                                <div className="space-y-4">
                                    <StaticField label={t('crafts.tables.org')} value={data.org_id} isRTL={isRTL} />
                                    <StaticField label={t('common.status')} value={data.status} isRTL={isRTL} />
                                </div>
                            </Section>
                        </div>
                    )}

                    {activeTab === 'SKILLS_AND_LEVELS' && (
                        <div className="bg-white border rounded-sm shadow-sm overflow-hidden">
                            <div className="p-3 bg-[#1a3353] text-white font-black uppercase flex justify-between items-center shadow-lg">
                                <span className="text-[10px] tracking-widest flex items-center gap-2"><List size={14}/> {t('crafts.tabs.skills')}</span>
                                <button onClick={addSkill} disabled={!isEditable} className="bg-white/10 hover:bg-white/20 p-1.5 px-4 rounded-full flex items-center gap-2 transition-all text-[9px] border border-white/20 shadow-sm active:scale-95 disabled:opacity-50">
                                    <Plus size={12}/> {t('crafts.actions.addSkill')}
                                </button>
                            </div>
                            <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
                                <thead className="bg-slate-50 text-[9px] font-black text-gray-400 uppercase border-b">
                                    <tr>
                                        <th className={`p-3 ${isRTL ? 'border-l' : 'border-r'}`}>{t('crafts.fields.skillLevel')}</th>
                                        <th className={`p-3 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('crafts.fields.hourlyRate')}</th>
                                        <th className="p-3 w-10 text-center"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-[12px] font-bold">
                                    {(data.skills || []).map((skill, idx) => (
                                        <tr key={idx} className="hover:bg-blue-50 transition-colors">
                                            <td className={`p-2 border-x ${isRTL ? 'border-l' : 'border-r'}`}>
                                                <input className={`w-full outline-none bg-transparent text-blue-900 ${isRTL ? 'text-right' : 'text-left'}`} 
                                                    placeholder={t('crafts.placeholders.enterLevel')} 
                                                    value={skill.skill_level} 
                                                    disabled={!isEditable}
                                                    onChange={e => {
                                                        const newList = [...data.skills]; 
                                                        newList[idx].skill_level = e.target.value; 
                                                        setData({...data, skills: newList});
                                                    }} 
                                                />
                                            </td>
                                            <td className={`p-2 border-x bg-gray-50/30 ${isRTL ? 'border-l' : 'border-r'}`}>
                                                <input type="number" className="w-full outline-none bg-transparent text-center text-green-700 font-mono font-black" 
                                                    value={skill.standard_rate} 
                                                    disabled={!isEditable}
                                                    onChange={e => {
                                                        const newList = [...data.skills]; 
                                                        newList[idx].standard_rate = parseFloat(e.target.value) || 0; 
                                                        setData({...data, skills: newList});
                                                    }} 
                                                />
                                            </td>
                                            <td className="p-2 text-center">
                                                <button onClick={() => removeSkill(idx)} disabled={!isEditable} className="text-gray-300 hover:text-red-500 transition-colors disabled:opacity-50">
                                                    <Trash2 size={16}/>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {(!data.skills || data.skills.length === 0) && (
                                        <tr><td colSpan="3" className="p-12 text-center text-gray-300 italic uppercase font-black tracking-widest">{t('crafts.messages.noSkills')}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- المكونات الداخلية الصافية ---
const HeaderField = ({ label, value, onChange, width, isBold, disabled, placeholder, isRTL }) => (
    <div className={`flex flex-col px-4 border-gray-200 shrink-0 ${isRTL ? 'border-l text-right last:border-0' : 'border-r text-left last:border-0'}`}>
        <label className="text-[0.8em] text-gray-400 font-black uppercase tracking-widest mb-0.5 italic leading-none">{label}</label>
        <input className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-blue-900 text-[13px] tracking-tighter' : 'text-blue-700 text-[12px]'} bg-transparent transition-all focus:border-blue-500 placeholder:opacity-30 placeholder:lowercase placeholder:font-normal placeholder:italic ${isRTL ? 'text-right' : 'text-left'}`} 
               value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} placeholder={placeholder} />
    </div>
);

const Section = ({ title, children, icon: Icon, isRTL }) => (
    <div className="bg-white border rounded shadow-sm transition-all hover:shadow-md border-gray-200 overflow-hidden">
        <div className={`p-3 bg-slate-50 border-b border-gray-100 font-black text-[#1a3353] uppercase text-[10px] tracking-widest flex items-center gap-2 italic`}>
            {Icon && <Icon size={14} className="text-orange-600"/>} {title}
        </div>
        <div className="p-6">{children}</div>
    </div>
);

const StaticField = ({ label, value, isRTL }) => (
    <div className={`flex flex-col border-b border-gray-100 py-1.5 px-1 bg-white/40 shadow-inner rounded-sm mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
        <label className="text-[0.8em] font-black text-gray-400 uppercase tracking-widest italic">{label}</label>
        <div className="font-black uppercase tracking-tighter text-blue-900 text-[12px] mt-0.5 italic">{value || '---'}</div>
    </div>
);

export default CraftDetails;